import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useParams, Link } from "react-router-dom";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Modal,
    ModalBody,
    Row,
    Table,
} from "reactstrap";
import AchievementCustomTable from "components/Common/AchievementCustomTable";
import {
    getIncentiveMonthlyTwoCandidates,
    getIncentiveMonthlyTwoDetails,
} from "model/IncentiveMonthlyModel";
import Loaders from "components/Common/Loaders";
import { urlEndPoint } from "helpers/GeneralHelper";

const MonthlyDetailsTwo = () => {
    const { id } = useParams();
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [monthlyDetailsData, setMonthlyDetailsData] = useState([]);
    const [candidateModalOpen, setCandidateModalOpen] = useState(false);
    const [candidateModalTitle, setCandidateModalTitle] = useState("");
    const [candidateList, setCandidateList] = useState([]);
    const [candidateLoading, setCandidateLoading] = useState(false);
    const columns = [
        { Header: "Client", accessor: "client" },
        { Header: "Target", accessor: "target" },
        { Header: "Rs/Per", accessor: "rs" },
        { Header: "Negative", accessor: "negative" },
        { Header: "Achieve", accessor: "achieve" },
        { Header: "WorkDays", accessor: "workdays" },
        { Header: "Count", accessor: "count" },
        { Header: "Incentive", accessor: "incentive" },
    ];
    const toggleCandidateModal = () => setCandidateModalOpen((prev) => !prev);

    const handleClientClick = async (detail = {}) => {
        const detailsId = detail?.incentiveClientDetailsId || "";
        setCandidateModalTitle(String(detail?.clientName || "").toUpperCase());
        setCandidateList([]);
        setCandidateModalOpen(true);
        if (!detailsId) return;

        setCandidateLoading(true);
        const response = await getIncentiveMonthlyTwoCandidates(detailsId);
        const responseData = response?.data || {};
        const records = Array.isArray(responseData?.data)
            ? responseData?.data
            : Array.isArray(responseData?.records)
                ? responseData?.records
                : Array.isArray(responseData)
                    ? responseData
                    : [];
        setCandidateList(records);
        setCandidateLoading(false);
    };

    const getMonthlyDetails = async () => {
        if (!id) {
            setMonthlyDetailsData([]);
            return;
        }
        setLoading(true);
        const response = await getIncentiveMonthlyTwoDetails(id);
        const responseData = response?.data || {};
        const records = Array.isArray(responseData?.data)
            ? responseData?.data
            : Array.isArray(responseData)
                ? responseData
                : [];

            // console.log('...records', records)

        const members = (Array.isArray(records) ? records : []).map((item) => ({
            userId: item?.userId || "",
            name: item?.userName || "User",
            rows: (Array.isArray(item?.details) ? item?.details : []).map((detail, index) => ({
                id: detail?.incentiveClientDetailsId || `${detail?.incentiveMonthDetailsId || item?.userId || "row"}-${index}`,
                client: (
                    <span
                        role="button"
                        tabIndex={0}
                        className="inc-client-link"
                        onClick={() => handleClientClick(detail)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleClientClick(detail);
                            }
                        }}
                    >
                        {detail?.clientName || ""}
                    </span>
                ),
                target: `${detail?.targetMin ?? ""} - ${detail?.targetMax ?? ""}`,
                rs: detail?.perAmount ?? "",
                negative: detail?.isNegative ? "Yes" : "No",
                achieve: detail?.achieve ?? 0,
                workdays: detail?.workDays ?? 0,
                count: detail?.incentiveCount ?? 0,
                incentive: detail?.totalAmount ?? detail?.incentiveAmount ?? 0,
            })),
        }));

        setMonthlyDetailsData([{ team: "Monthly Incentive", teamLead: "", members }]);
        setLoading(false);
    };

    useEffect(() => {
        getMonthlyDetails();
    }, [id]);

    // Filtering
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return monthlyDetailsData;

        return monthlyDetailsData.filter((team) =>
            team.members.some((member) =>
                member.rows.some((row) =>
                    Object.values(row)
                        .some((val) =>
                            String(val).toLowerCase().includes(searchQuery.toLowerCase())
                        )
                )
            )
        );
    }, [searchQuery, monthlyDetailsData]);

    const hasTableRows = useMemo(
        () =>
            (Array.isArray(filteredData) ? filteredData : []).some((team) =>
                (Array.isArray(team?.members) ? team.members : []).some(
                    (member) => Array.isArray(member?.rows) && member.rows.length > 0
                )
            ),
        [filteredData]
    );

    return (
        <React.Fragment>
            <div className="page-content incentive-monthly-details-page">
                <Container fluid>
                    <Card className="inc-details-content-card border-0 shadow-sm">
                        <CardBody className="p-0">
                            {loading ? (
                                <div className="p-4">
                                    <Loaders />
                                </div>
                            ) : hasTableRows ? (
                                <div className="p-3">
                                    <AchievementCustomTable
                                        className="inc-achievement-table-card"
                                        columns={columns}
                                        data={filteredData}
                                        isActionRequired={false}
                                        pageName="IncentiveMonthlyDetailsTwo"
                                    />
                                </div>
                            ) : (
                                <div className="inc-empty-state p-5 text-center text-muted">
                                    <i className="bx bx-line-chart display-4 d-block mb-2 opacity-25" />
                                    <span className="fw-semibold">No incentive data for this month.</span>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    <Modal
                        isOpen={candidateModalOpen}
                        toggle={toggleCandidateModal}
                        backdrop="static"
                        className="modal-full-height"
                        centered
                        size="xl"
                    >
                        <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="fw-bold mb-0">{candidateModalTitle || "CANDIDATES"}</h5>
                                <Button close onClick={toggleCandidateModal} />
                            </div>
                            <hr />
                            <div className="table-responsive">
                                <Table bordered hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Candidate</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!candidateLoading && candidateList?.length ? (
                                            candidateList.map((item, idx) => (
                                                <tr key={item?.id || item?.candidateId || idx}>
                                                    <td>
                                                        {(
                                                            `${item?.firstName || ""} ${item?.lastName || ""}`.trim() ||
                                                            item?.candidate ||
                                                            item?.candidateName ||
                                                            item?.name ||
                                                            "-"
                                                        )}
                                                    </td>
                                                    <td>{item?.email || "-"}</td>
                                                    <td>{item?.phoneNumber || item?.phone || "-"}</td>
                                                </tr>
                                            ))
                                        ) : !candidateLoading ? (
                                            <tr>
                                                <td colSpan={3} className="text-center text-muted">
                                                    No data found
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <td colSpan={3} className="text-center text-muted">
                                                    Loading...
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </ModalBody>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    );
};

MonthlyDetailsTwo.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(MonthlyDetailsTwo);
